import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MaterialModule } from './material.module';
import { TimeoutComponent } from './timeout/timeout.component';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { SharedService } from './shared-service/shared.service';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { LoaderComponent } from './loader/loader.component';
import { LoaderService } from './loader/loader.service';
import ApiInterceptor from './api.interceptor';
import { AuthGuard } from './auth-guard/auth-guard.service';
import { DragAndDropDirective } from './directives/dragAndDrop.directive';
import { PipesModule } from './pipes/pipes.module';
import { RouterConstants } from './constants/router-constants';
import { ReportIssuesModule } from './report-issues/report-issues.module';
import { QuillModule } from 'ngx-quill';
import './constants/quill-export-security';
import { AgGridModule } from 'ag-grid-angular';
import { GlobalErrorHandler } from './services/global-error-handler.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IdleExpiry, NgIdleModule, SimpleExpiry } from '@ng-idle/core';
import { InfoModule } from './info-details/info-details.module';
import { CallbackModule } from './callback/callback.module';
import { OAuthModule } from 'angular-oauth2-oidc';
import { CommonModule, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { ServiceWorkerModule } from '@angular/service-worker';
import { SimpleCellRendererComponent } from './common-renderer/simple-cell-renderer/simple-cell-renderer.component';
import { RouterModule, RouterOutlet, RouteReuseStrategy, ActivatedRouteSnapshot, DetachedRouteHandle } from '@angular/router';
import { ChatbotModule } from './chatbot/chatbot.module';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import { VoteComponent } from './vote/vote.component';
import { DataRefreshRendererSharedModule } from './data-refresh-renderer/data-refresh-renderer-shared.module';

export class NoReuseStrategy implements RouteReuseStrategy {
    shouldDetach(route: ActivatedRouteSnapshot): boolean { return false; }
    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void { }
    shouldAttach(route: ActivatedRouteSnapshot): boolean { return false; }
    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null { return null; }
    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        const getUrl = (route: ActivatedRouteSnapshot): string[] => {
            const path = route.routeConfig && route.routeConfig.path ? [route.routeConfig.path] : [];
            if (route.firstChild) {
                return path.concat(getUrl(route.firstChild));
            }
            return path;
        };
        const futureUrl = '/' + getUrl(future).join('/');
        const currUrl = '/' + getUrl(curr).join('/');
        // Force reload when navigating to or within /supplier-new-home
        if (futureUrl === '/supplier-new-home' && currUrl === '/supplier-new-home') {
            return false;
        }
        // Reuse for all other routes
        return future.routeConfig === curr.routeConfig;
    }
}
@NgModule({
    declarations: [
        AppComponent,
        TimeoutComponent,
        LoaderComponent,
        DragAndDropDirective,
        SimpleCellRendererComponent,
        VoteComponent
    ],
    bootstrap: [AppComponent],
    exports: [DragAndDropDirective],
    imports: [
        CommonModule,
        RouterOutlet,
        RouterModule,
        BrowserModule,
        AppRoutingModule,
        ReactiveFormsModule,
        FormsModule,
        BrowserAnimationsModule,
        MaterialModule,
        NgbModule,
        PipesModule,
        NgIdleKeepaliveModule.forRoot(),
        NgIdleModule.forRoot(),
        QuillModule.forRoot(),
        FroalaEditorModule.forRoot(),
        FroalaViewModule.forRoot(),
        ReportIssuesModule,
        InfoModule,
        CallbackModule,
        ChatbotModule,
        OAuthModule.forRoot({
            resourceServer: {
                allowedUrls: [
                    'http://localhost:8080',
                    'https://scworkbench-dev-service.cloudapps.cisco.com/gsmscw/api'
                ],
                sendAccessToken: true,
            },
        }),
        AgGridModule,
        DataRefreshRendererSharedModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
            // enabled: environment.production,
            enabled: (window as any).__env && (window as any).__env.enableServiceWorker === true,
            // Register the ServiceWorker as soon as the application is stable
            // or after 30 seconds (whichever comes first).
            registrationStrategy: 'registerWhenStable:30000'
        })
    ],
    providers: [
        SharedService,
        LoaderService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ApiInterceptor,
            multi: true
        },
        {
            provide: IdleExpiry,
            useClass: SimpleExpiry
        },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        AuthGuard,
        RouterConstants,
        provideHttpClient(withInterceptorsFromDi()),
        { provide: RouteReuseStrategy, useClass: NoReuseStrategy }
    ]
})
export class AppModule { }
